// Frontend: MoreDropDown.jsx
import React, { useState, useRef, useEffect } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import Classes from './RecordTable.module.css';

/* ---------- Calendar helpers ---------- */
function pad(n) { return String(n).padStart(2, '0'); }
function toICSDate(dt) {
  const d = new Date(dt);
  return (
    d.getUTCFullYear().toString() +
    pad(d.getUTCMonth() + 1) +
    pad(d.getUTCDate()) +
    'T' +
    pad(d.getUTCHours()) +
    pad(d.getUTCMinutes()) +
    pad(d.getUTCSeconds()) +
    'Z'
  );
}
function toGoogleDateRange(startISO, endISO) {
  return `${toICSDate(startISO)}/${toICSDate(endISO)}`;
}
function safeText(s) { return (s || '').toString().trim(); }

function defaultDurationMs(ms = 60 * 60 * 1000) { return ms; } // 1 hour default
function isIOS() {
  return /iPad|iPhone|iPod/.test(window.navigator.userAgent);
}

/** Build location single-event ICS text */
function buildLocation({ uid, title, desc, location, start, end }) {
  return [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//FuelClubs//Appointments//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${uid}`,
    `DTSTAMP:${toICSDate(new Date())}`,
    `DTSTART:${toICSDate(start)}`,
    `DTEND:${toICSDate(end)}`,
    `SUMMARY:${title.replace(/\n/g, ' ')}`,
    `DESCRIPTION:${desc.replace(/\n/g, ' ')}`,
    `LOCATION:${location.replace(/\n/g, ' ')}`,
    'END:VEVENT',
    'END:VCALENDAR',
    ''
  ].join('\r\n');
}

/** iOS: open Calendar add sheet via data: URL; others: download .ics */
function openAppleCalendar(icsText, filename = 'FuelClubs-Appointment.ics') {
  if (isIOS()) {
    const dataUrl = 'data:text/calendar;charset=utf-8,' + encodeURIComponent(icsText);
    window.location.href = dataUrl;
  } else {
    const blob = new Blob([icsText], { type: 'text/calendar;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }
}

export default function MoreDropDown({
  booking,
  handleopenReschedulePopup,
  handleOpenConfirmPopup,
  handleOpenTipPopup,
  disabled
}) {
  const [dropDown, setDropDown] = useState(false);
  const dropdownRef = useRef(null);
  const navigateTo = useNavigate();

  useEffect(() => {
    function handleClickOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropDown(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const createGoogleCalendarUrl = () => {
    const title = safeText('Fuel Delivery');
    const details = safeText('Your Fuel Clubs service');
    const location = safeText(buildLocation(booking));
    const start = booking?.appointment?.date ? new Date(booking.appointment.date) : new Date();
    const end = new Date(start.getTime() + defaultDurationMs());
    const params = new URLSearchParams({
      action: 'TEMPLATE',
      text: title,
      details,
      location,
      dates: toGoogleDateRange(start, end),
    });
    return `https://calendar.google.com/calendar/render?${params.toString()}`;
  };

  const openGoogle = () => {
    window.open(createGoogleCalendarUrl(), '_blank', 'noopener,noreferrer');
    setDropDown(false);
  };

  const addToApple = () => {
    const uid = booking?._id || `${Date.now()}@fuelclubs`;
    const title = safeText('Fuel Delivery');
    const desc = safeText('Your Fuel Clubs service');
    const location = safeText(buildLocation(booking));
    const start = booking?.appointment?.date ? new Date(booking.appointment.date) : new Date();
    const end = new Date(start.getTime() + defaultDurationMs());

    const ics = buildLocation({ uid, title, desc, location, start, end });
    openAppleCalendar(ics, 'FuelClubs-Appointment.ics');
    setDropDown(false);
  };

  const isCompleted = (booking?.status === 'completed');

  return (
    <div className={Classes.dropDown} ref={dropdownRef}>
      <div className="flexAlign10 pointer mr10" onClick={() => !disabled && setDropDown(!dropDown)}>
        <div className={Classes.dropdownIcon}>
          <svg xmlns="http://www.w3.org/2000/svg" height='15px' viewBox="0 0 128 512">
            <path d="M64 360a56 56 0 1 0 0 112 56 56 0 1 0 0-112zm0-160a56 56 0 1 0 0 112 56 56 0 1 0 0-112zM120 96A56 56 0 1 0 8 96a56 56 0 1 0 112 0z" />
          </svg>
        </div>
      </div>

      <AnimatePresence>
        {dropDown && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -5 }}
            transition={{ duration: 0.15 }}
            className={Classes.dropDownBody}
            style={{ top: '13px', right: '30px' }}
          >
            <div className={Classes.dropDownBodyInner}>
              <div
                className={Classes.dropDownElem}
                style={{ padding: '5px 10px' }}
                onClick={() => { navigateTo('/messages'); setDropDown(false); }}
              >
                <h5>Message Company</h5>
              </div>

              <div className={Classes.dropDownLine}></div>

              <div
                className={Classes.dropDownElem}
                style={{ padding: '5px 10px' }}
                onClick={() => { handleopenReschedulePopup?.(); setDropDown(false); }}
              >
                <h5>Change Appointment</h5>
              </div>

              <div className={Classes.dropDownLine}></div>

              <div
                className={Classes.dropDownElem}
                style={{ padding: '5px 10px', color: 'red' }}
                onClick={() => { handleOpenConfirmPopup?.(); setDropDown(false); }}
              >
                <h5>Cancel Appointment</h5>
              </div>

              {/* Add tip — only when completed */}
              {isCompleted && (
                <>
                  <div className={Classes.dropDownLine}></div>
                  <div
                    className={Classes.dropDownElem}
                    style={{ padding: '5px 10px' }}
                    onClick={() => { handleOpenTipPopup?.(); setDropDown(false); }}
                  >
                    <h5>Add tip</h5>
                  </div>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <input type="hidden" aria-label="Without label" readOnly />
    </div>
  );
}
